/**
 * One server-to-client socket as a React hook: the connection logic shared by
 * a job's socket (`useJobSocket`) and a design's (`modeling/useModelSocket`).
 * The socket is a view: the server greets with `hello` on every connect, so
 * reconnecting is always safe, and closing it never affects what it shows.
 *
 * Reconnection: exponential backoff from 1 s to 30 s, reset by the first
 * message of a connection; no reconnect after an auth close (4401/4403) or
 * once `isTerminal()` says nothing more will come. The page polls REST while
 * the socket is not open.
 *
 * The socket constructor is injected through `SocketFactoryContext` so tests
 * drive a fake (`src/test/fakeSocket.ts`) instead of a network.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { API_URL } from "../api/client";

/** The slice of `WebSocket` the hook uses; a fake implements the same. */
export interface SocketLike {
  onopen: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
  close(): void;
}

export type SocketFactory = (url: string) => SocketLike;

export const defaultSocketFactory: SocketFactory = (url) => new WebSocket(url);

export const SocketFactoryContext =
  createContext<SocketFactory>(defaultSocketFactory);

/** `connecting` is the first attempt, `reconnecting` every later one (the
 *  page shows its quiet mark), `closed` is final (auth refused, or nothing
 *  more will come). */
export type SocketStatus = "connecting" | "open" | "reconnecting" | "closed";

export const CLOSE_UNAUTHENTICATED = 4401;
export const CLOSE_FORBIDDEN = 4403;
export const BACKOFF_MIN_MS = 1_000;
export const BACKOFF_MAX_MS = 30_000;

/** The socket address of an API path such as `/v1/models/<id>/ws`. */
export function socketUrl(path: string): string {
  return `${API_URL.replace(/^http/, "ws")}${path}`;
}

export function backoffMs(attempt: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** attempt);
}

export interface SocketEvent {
  type: string;
  [key: string]: unknown;
}

export function parseSocketEvent(data: unknown): SocketEvent | null {
  if (typeof data !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (parsed && typeof parsed === "object" && "type" in parsed) {
      return parsed as SocketEvent;
    }
  } catch {
    // Not our JSON; ignore the message rather than crash the page.
  }
  return null;
}

export function useSocket(
  path: string,
  onEvent: (event: SocketEvent) => void,
  opts: {
    enabled?: boolean;
    /** Asked when the socket closes: true stops reconnecting. */
    isTerminal?: () => boolean;
  } = {},
): SocketStatus {
  const { enabled = true } = opts;
  const factory = useContext(SocketFactoryContext);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const isTerminalRef = useRef(opts.isTerminal);
  isTerminalRef.current = opts.isTerminal;
  const [status, setStatus] = useState<SocketStatus>(
    enabled ? "connecting" : "closed",
  );

  useEffect(() => {
    if (!enabled) return;
    let socket: SocketLike | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let stopped = false;

    const connect = () => {
      const s = factory(socketUrl(path));
      socket = s;
      let greeted = false;
      s.onopen = () => {
        setStatus("connecting");
      };
      s.onmessage = (ev) => {
        const event = parseSocketEvent(ev.data);
        if (!greeted) {
          // Open only once the server has said hello: a socket that opens and is closed
          // at once (on qa, every one, for weeks) must back off and leave REST polling
          // on, not reset both every two seconds.
          greeted = true;
          attempt = 0;
          setStatus("open");
        }
        if (event) onEventRef.current(event);
      };
      s.onerror = () => {
        // A close always follows an error; reconnection is decided there.
      };
      s.onclose = (ev) => {
        if (stopped || socket !== s) return;
        const code = ev?.code;
        if (
          code === CLOSE_UNAUTHENTICATED ||
          code === CLOSE_FORBIDDEN ||
          isTerminalRef.current?.()
        ) {
          setStatus("closed");
          return;
        }
        setStatus("reconnecting");
        timer = setTimeout(connect, backoffMs(attempt));
        attempt += 1;
      };
    };

    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [path, factory, enabled]);

  return status;
}
